import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../hooks/useSocket';
import './Inbox.css';

// ─── helpers ─────────────────────────────────────────────────────────────────

function timeAgo(date) {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function windowState(conversation) {
  if (!conversation?.session_expires_at) return { open: false, label: 'No session' };
  const ms = new Date(conversation.session_expires_at).getTime() - Date.now();
  if (ms <= 0) return { open: false, label: 'Window closed' };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { open: true, label: h > 0 ? `${h}h ${m}m left` : `${m}m left` };
}

function dayLabel(date) {
  const d = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const STATUS_TICKS = { pending: '🕓', sent: '✓', delivered: '✓✓', read: '✓✓', failed: '✗' };

// Reply-priority chats pin above everything else, then newest first —
// mirrors the server-side sort so live socket updates keep the same order.
function sortConvs(list) {
  return [...list].sort((a, b) =>
    (b.priority === true) - (a.priority === true) ||
    new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function Inbox() {
  const { user } = useAuth();
  const [statusTab, setStatusTab] = useState('open');
  const [assignedFilter, setAssignedFilter] = useState('all'); // all | me | unassigned
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce so we don't fire a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [conversations, setConversations] = useState([]);
  const [counts, setCounts] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null); // full conversation doc
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [agents, setAgents] = useState([]);
  const [labels, setLabels] = useState([]);
  const [cannedReplies, setCannedReplies] = useState([]);
  const [showPanel, setShowPanel] = useState(false); // mobile contact panel
  const [showNewChat, setShowNewChat] = useState(false);
  const [mobileView, setMobileView] = useState('list'); // list | thread
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;
  const loadSeqRef = useRef(0); // guards against slow responses landing out of order

  // ── data loading ──
  const loadConversations = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      const params = { status: statusTab, limit: 50 };
      if (assignedFilter !== 'all') params.assigned = assignedFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const { data } = await api.get('/inbox/conversations', { params });
      if (seq !== loadSeqRef.current) return; // a newer request superseded this one
      setConversations(sortConvs(data.conversations));
      setCounts(data.counts || {});
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      toast.error(err.response?.data?.message || 'Failed to load conversations');
    }
  }, [statusTab, assignedFilter, debouncedSearch]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    api.get('/inbox/agents').then(r => setAgents(r.data)).catch(() => {});
    api.get('/inbox/labels').then(r => setLabels(r.data)).catch(() => {});
    api.get('/inbox/canned-replies').then(r => setCannedReplies(r.data)).catch(() => {});
  }, []);

  const openConversation = useCallback(async id => {
    setSelectedId(id);
    setMobileView('thread');
    try {
      const [convRes, msgRes] = await Promise.all([
        api.get(`/inbox/conversations/${id}`),
        api.get(`/inbox/conversations/${id}/messages`, { params: { limit: 30 } }),
      ]);
      setSelected(convRes.data);
      setMessages(msgRes.data.messages);
      setHasMore(msgRes.data.hasMore);
      api.post(`/inbox/conversations/${id}/read`).catch(() => {});
      setConversations(prev => prev.map(c => c._id === id ? { ...c, unread_count: 0 } : c));
    } catch (err) {
      toast.error('Failed to open conversation');
    }
  }, []);

  const loadOlder = async () => {
    if (!messages.length) return;
    try {
      const { data } = await api.get(`/inbox/conversations/${selectedId}/messages`, {
        params: { before: messages[0]._id, limit: 30 },
      });
      setMessages(prev => [...data.messages, ...prev]);
      setHasMore(data.hasMore);
    } catch { /* noop */ }
  };

  // ── live updates ──
  const onNewMessage = useCallback(({ conversation, message }) => {
    setConversations(prev => {
      const rest = prev.filter(c => c._id !== conversation._id);
      const existing = prev.find(c => c._id === conversation._id);
      // keep populated contact from the existing row (socket payload has raw ids)
      const merged = { ...conversation, contact: existing?.contact || conversation.contact, assigned_to: existing?.assigned_to ?? conversation.assigned_to };
      return sortConvs([merged, ...rest]);
    });
    if (String(conversation._id) === String(selectedIdRef.current)) {
      setMessages(prev => prev.some(m => m._id === message._id) ? prev : [...prev, message]);
      setSelected(prev => prev ? { ...prev, ...conversation, contact: prev.contact, assigned_to: prev.assigned_to, labels: prev.labels } : prev);
      api.post(`/inbox/conversations/${conversation._id}/read`).catch(() => {});
    }
  }, []);

  const onStatus = useCallback(({ conversationId, messageId, status }) => {
    if (String(conversationId) === String(selectedIdRef.current)) {
      setMessages(prev => prev.map(m => m._id === messageId ? { ...m, status } : m));
    }
  }, []);

  const onConvUpdated = useCallback(({ conversation }) => {
    setConversations(prev => sortConvs(prev.map(c =>
      c._id === conversation._id
        ? { ...c, ...conversation, contact: c.contact, assigned_to: c.assigned_to }
        : c
    )));
  }, []);

  useSocketEvent('message:new', onNewMessage);
  useSocketEvent('message:status', onStatus);
  useSocketEvent('conversation:updated', onConvUpdated);
  useSocketEvent('conversation:assigned', useCallback(() => { loadConversations(); }, [loadConversations]));

  // ── mutations ──
  const patchConversation = async updates => {
    try {
      const { data } = await api.patch(`/inbox/conversations/${selectedId}`, updates);
      setSelected(data);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  };

  const win = windowState(selected);

  return (
    <div className={`inbox-page mobile-${mobileView}`}>
      {/* ── left: conversation list ── */}
      <aside className="inbox-list">
        <div className="inbox-list-header">
          <div className="inbox-list-title">
            <h2>Inbox</h2>
            <button className="btn-new-chat" onClick={() => setShowNewChat(true)} title="Message a customer by phone number">＋ New chat</button>
          </div>
          <div className="inbox-search-wrap">
            <input
              className="inbox-search"
              placeholder="Search name, phone or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="inbox-search-clear" onClick={() => setSearch('')} title="Clear search">×</button>
            )}
          </div>
          {debouncedSearch.trim() && (
            <div className="inbox-search-hint">Showing matches from all tabs</div>
          )}
          <div className="inbox-tabs">
            {['open', 'pending', 'resolved'].map(s => (
              <button
                key={s}
                className={`inbox-tab ${statusTab === s ? 'active' : ''}`}
                onClick={() => setStatusTab(s)}
              >
                {s[0].toUpperCase() + s.slice(1)}
                {counts[s] > 0 && <span className="tab-count">{counts[s]}</span>}
              </button>
            ))}
          </div>
          <div className="inbox-pills">
            {[['all', 'All'], ['me', `Mine${counts.mine ? ` (${counts.mine})` : ''}`], ['unassigned', `Unassigned${counts.unassigned ? ` (${counts.unassigned})` : ''}`]].map(([k, label]) => (
              <button
                key={k}
                className={`inbox-pill ${assignedFilter === k ? 'active' : ''}`}
                onClick={() => setAssignedFilter(k)}
              >{label}</button>
            ))}
          </div>
        </div>
        <div className="inbox-conversations">
          {conversations.length === 0 && (
            debouncedSearch.trim()
              ? <div className="inbox-empty">No customers match “{debouncedSearch.trim()}”.<br />Try part of their name, phone number or email.</div>
              : <div className="inbox-empty">No conversations here yet.<br />Incoming WhatsApp messages will appear automatically.</div>
          )}
          {conversations.map(c => (
            <ConversationRow
              key={c._id}
              conversation={c}
              active={c._id === selectedId}
              onClick={() => openConversation(c._id)}
            />
          ))}
        </div>
      </aside>

      {/* ── center: thread ── */}
      <section className="inbox-thread">
        {!selected ? (
          <div className="thread-placeholder">
            <span className="thread-placeholder-icon">💬</span>
            <p>Select a conversation to start replying</p>
          </div>
        ) : (
          <>
            <div className="thread-header">
              <button className="btn-back" onClick={() => setMobileView('list')}>‹</button>
              <div className="thread-header-info" onClick={() => setShowPanel(p => !p)}>
                <div className="thread-avatar">{(selected.contact?.name || selected.contact?.phone || '?')[0].toUpperCase()}</div>
                <div>
                  <div className="thread-name">{selected.contact?.name || selected.contact?.phone}</div>
                  <div className="thread-sub">{selected.contact?.phone}</div>
                </div>
              </div>
              <span className={`window-chip ${win.open ? 'open' : 'closed'}`}>{win.label}</span>
              <button className="btn-panel-toggle" onClick={() => setShowPanel(p => !p)} title="Contact details">ⓘ</button>
            </div>

            <MessageList messages={messages} hasMore={hasMore} onLoadOlder={loadOlder} agents={agents} />

            <Composer
              conversation={selected}
              windowOpen={win.open}
              cannedReplies={cannedReplies}
              onCannedCreated={r => setCannedReplies(prev => [...prev, r])}
              onSent={({ message, conversation }) => {
                setMessages(prev => prev.some(m => m._id === message._id) ? prev : [...prev, message]);
                setSelected(prev => prev ? { ...prev, mode: conversation.mode } : prev);
              }}
            />
          </>
        )}
      </section>

      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onStarted={conv => {
            setShowNewChat(false);
            loadConversations();
            openConversation(conv._id);
          }}
        />
      )}

      {/* ── right: contact panel ── */}
      {selected && (
        <ContactPanel
          visible={showPanel}
          onClose={() => setShowPanel(false)}
          conversation={selected}
          agents={agents}
          labels={labels}
          currentUser={user}
          onPatch={patchConversation}
        />
      )}
    </div>
  );
}

// ─── conversation list row ───────────────────────────────────────────────────

function ConversationRow({ conversation: c, active, onClick }) {
  const name = c.contact?.name || c.contact?.phone || 'Unknown';
  const win = windowState(c);
  return (
    <button className={`conv-row ${active ? 'active' : ''}`} onClick={onClick}>
      <div className="conv-avatar">{name[0].toUpperCase()}</div>
      <div className="conv-body">
        <div className="conv-top">
          <span className="conv-name">{name}</span>
          <span className="conv-time">{timeAgo(c.last_message_at)}</span>
        </div>
        <div className="conv-bottom">
          <span className="conv-preview">{c.last_message_preview || '—'}</span>
          {c.unread_count > 0 && <span className="conv-unread">{c.unread_count}</span>}
        </div>
        <div className="conv-meta">
          {c.priority && <span className="conv-priority">↩ Priority reply</span>}
          {c.assigned_to && <span className="conv-assignee">👤 {c.assigned_to.name?.split(' ')[0]}</span>}
          {c.mode === 'human' && <span className="conv-mode">Human</span>}
          {!win.open && <span className="conv-window-closed">Window closed</span>}
        </div>
      </div>
    </button>
  );
}

// ─── message thread ──────────────────────────────────────────────────────────

function MessageList({ messages, hasMore, onLoadOlder }) {
  const bottomRef = useRef(null);
  const lastId = messages.length ? messages[messages.length - 1]._id : null;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [lastId]);

  let lastDay = null;
  return (
    <div className="thread-messages">
      {hasMore && <button className="btn-load-older" onClick={onLoadOlder}>Load older messages</button>}
      {messages.map(m => {
        const day = dayLabel(m.createdAt || m.sent_at);
        const showDay = day !== lastDay;
        lastDay = day;
        return (
          <React.Fragment key={m._id}>
            {showDay && <div className="day-separator"><span>{day}</span></div>}
            <MessageBubble message={m} />
          </React.Fragment>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

function MessageBubble({ message: m }) {
  if (m.direction === 'internal') {
    return (
      <div className="msg-note">
        <span className="msg-note-label">📌 Note{m.sender?.agent?.name ? ` — ${m.sender.agent.name}` : ''}</span>
        <p>{m.body}</p>
        <span className="msg-time">{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    );
  }

  const out = m.direction === 'out';
  const senderLabel = out && m.sender?.kind !== 'agent'
    ? { bot: '🤖 Bot', automation: '🤖 Automation', broadcast: '📢 Broadcast', system: 'System', api: '🔌 API' }[m.sender?.kind]
    : (out && m.sender?.agent?.name ? m.sender.agent.name : null);

  return (
    <div className={`msg ${out ? 'msg-out' : 'msg-in'}`}>
      {senderLabel && <span className="msg-sender">{senderLabel}</span>}
      {m.type === 'template' && <span className="msg-template-tag">Template{m.template_name ? `: ${m.template_name}` : ''}</span>}
      {m.media?.wa_media_id ? <MediaAttachment media={m.media} /> : null}
      {m.body && <p className="msg-body">{m.body}</p>}
      <span className="msg-time">
        {new Date(m.createdAt || m.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        {out && <span className={`msg-tick ${m.status}`}> {STATUS_TICKS[m.status] || ''}</span>}
      </span>
      {m.status === 'failed' && m.error && <span className="msg-error">{m.error}</span>}
    </div>
  );
}

// Meta media needs the JWT to proxy through the server, so <img src> won't
// work directly — fetch as a blob on demand instead.
function MediaAttachment({ media }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const isImage = media.mime_type?.startsWith('image/');
  const isAudio = media.mime_type?.startsWith('audio/');
  const isVideo = media.mime_type?.startsWith('video/');

  const load = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/inbox/media/${media.wa_media_id}`, { responseType: 'blob' });
      setUrl(URL.createObjectURL(resp.data));
    } catch {
      toast.error('Could not load media (it may have expired)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isImage) load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [media.wa_media_id]); // load images eagerly once per attachment

  if (url) {
    if (isImage) return (
      <>
        <img
          className="msg-media-img clickable"
          src={url}
          alt={media.caption || 'attachment'}
          title="Click to view"
          onClick={() => setViewerOpen(true)}
        />
        {viewerOpen && <ImageViewer url={url} media={media} onClose={() => setViewerOpen(false)} />}
      </>
    );
    if (isAudio) return <audio controls src={url} className="msg-media-audio" />;
    if (isVideo) return <video controls src={url} className="msg-media-video" />;
    return <a href={url} download={media.filename || 'file'} className="msg-media-file">📎 {media.filename || 'Download file'}</a>;
  }
  return (
    <button className="msg-media-load" onClick={load} disabled={loading}>
      {loading ? 'Loading…' : `📎 ${media.filename || media.mime_type || 'Load attachment'}`}
    </button>
  );
}

// Inbound WhatsApp images carry no filename — derive one from the mime type.
function mediaFilename(media) {
  if (media.filename) return media.filename;
  const ext = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' }[media.mime_type] || 'jpg';
  return `whatsapp-image-${media.wa_media_id || Date.now()}.${ext}`;
}

// Full-screen viewer for image attachments: click outside or Esc to close,
// download keeps the already-fetched blob (no second request).
function ImageViewer({ url, media, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-bar" onClick={e => e.stopPropagation()}>
        <a className="viewer-btn" href={url} download={mediaFilename(media)} title="Download image">⬇ Download</a>
        <button className="viewer-btn viewer-close" onClick={onClose} title="Close (Esc)">×</button>
      </div>
      <img
        className="image-viewer-img"
        src={url}
        alt={media.caption || 'attachment'}
        onClick={e => e.stopPropagation()}
      />
      {media.caption && <div className="image-viewer-caption" onClick={e => e.stopPropagation()}>{media.caption}</div>}
    </div>
  );
}

// ─── composer ────────────────────────────────────────────────────────────────

// Mirrors the server-side INBOX_MEDIA_TYPES map (WhatsApp free-form limits).
const ATTACH_TYPES = {
  'image/jpeg': { kind: 'image', max: 5 * 1024 * 1024 },
  'image/png':  { kind: 'image', max: 5 * 1024 * 1024 },
  'video/mp4':  { kind: 'video', max: 16 * 1024 * 1024 },
  'application/pdf': { kind: 'document', max: 100 * 1024 * 1024 },
  'text/plain': { kind: 'document', max: 100 * 1024 * 1024 },
  'text/csv':   { kind: 'document', max: 100 * 1024 * 1024 },
  'application/msword': { kind: 'document', max: 100 * 1024 * 1024 },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { kind: 'document', max: 100 * 1024 * 1024 },
  'application/vnd.ms-excel': { kind: 'document', max: 100 * 1024 * 1024 },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { kind: 'document', max: 100 * 1024 * 1024 },
  'application/vnd.ms-powerpoint': { kind: 'document', max: 100 * 1024 * 1024 },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { kind: 'document', max: 100 * 1024 * 1024 },
};
const ATTACH_ACCEPT = Object.keys(ATTACH_TYPES).join(',');

function fileSizeLabel(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(Math.round(bytes / 1024), 1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function Composer({ conversation, windowOpen, cannedReplies, onCannedCreated, onSent }) {
  const [tab, setTab] = useState('reply'); // reply | note
  const [text, setText] = useState('');
  const [attachment, setAttachment] = useState(null); // File
  const [sending, setSending] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { setText(''); setTab('reply'); setAttachment(null); }, [conversation._id]);

  // Image thumbnail for the pending attachment
  const previewUrl = React.useMemo(
    () => (attachment && attachment.type.startsWith('image/') ? URL.createObjectURL(attachment) : null),
    [attachment]
  );
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const pickFile = e => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    const spec = ATTACH_TYPES[file.type];
    if (!spec) return toast.error('Unsupported file type. Use JPEG/PNG images, MP4 video, or PDF/Office/text documents.');
    if (file.size > spec.max) return toast.error(`File too large — WhatsApp allows up to ${Math.round(spec.max / 1024 / 1024)} MB for ${spec.kind}s.`);
    setAttachment(file);
  };

  const showCanned = tab === 'reply' && text.startsWith('/');
  const cannedMatches = showCanned
    ? cannedReplies.filter(r =>
        (r.shortcut || r.title).toLowerCase().includes(text.slice(1).toLowerCase()))
    : [];

  const send = async () => {
    const body = text.trim();
    const hasAttachment = tab === 'reply' && attachment;
    if ((!body && !hasAttachment) || sending) return;
    setSending(true);
    try {
      if (tab === 'note') {
        const { data } = await api.post(`/inbox/conversations/${conversation._id}/notes`, { text: body });
        onSent({ message: data.message, conversation });
      } else if (hasAttachment) {
        const form = new FormData();
        form.append('file', attachment);
        if (body) form.append('caption', body); // text becomes the media caption
        const { data } = await api.post(`/inbox/conversations/${conversation._id}/media`, form);
        if (data.held) {
          toast('Attachment is on hold until tokens are added', { icon: '⏸' });
          window.dispatchEvent(new Event('token-balance-refresh'));
        }
        else onSent(data);
        setAttachment(null);
      } else {
        const { data } = await api.post(`/inbox/conversations/${conversation._id}/messages`, { type: 'text', text: body });
        if (data.held) {
          toast('Message is on hold until tokens are added', { icon: '⏸' });
          window.dispatchEvent(new Event('token-balance-refresh'));
        }
        else onSent(data);
      }
      setText('');
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error('The 24-hour window has closed — send a template instead.');
        setShowTemplates(true);
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || 'Send failed');
      }
    } finally {
      setSending(false);
    }
  };

  const saveCanned = async () => {
    const body = text.trim();
    if (!body) return toast.error('Type the reply text first');
    const title = window.prompt('Name this saved reply:');
    if (!title) return;
    try {
      const { data } = await api.post('/inbox/canned-replies', {
        title, body, shortcut: title.toLowerCase().replace(/\s+/g, '-'),
      });
      onCannedCreated(data);
      toast.success('Saved reply created');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save reply');
    }
  };

  const replyLocked = tab === 'reply' && !windowOpen;

  return (
    <div className="composer">
      {cannedMatches.length > 0 && (
        <div className="canned-picker">
          {cannedMatches.slice(0, 6).map(r => (
            <button key={r._id} className="canned-item" onClick={() => { setText(r.body); textareaRef.current?.focus(); }}>
              <strong>/{r.shortcut || r.title}</strong>
              <span>{r.body.slice(0, 80)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="composer-tabs">
        <button className={`composer-tab ${tab === 'reply' ? 'active' : ''}`} onClick={() => setTab('reply')}>Reply</button>
        <button className={`composer-tab note ${tab === 'note' ? 'active' : ''}`} onClick={() => setTab('note')}>Internal note</button>
        {tab === 'reply' && (
          <button className="composer-action" onClick={() => setShowTemplates(true)} title="Send a template message">📋 Template</button>
        )}
        {tab === 'reply' && text.trim() && (
          <button className="composer-action" onClick={saveCanned} title="Save as canned reply">＋ Save reply</button>
        )}
      </div>

      {replyLocked ? (
        <div className="composer-locked">
          <p>
            {conversation.session_expires_at
              ? 'The 24-hour reply window has closed. WhatsApp only allows approved template messages now.'
              : 'WhatsApp requires an approved template message to start a conversation. Free-form replies unlock once the customer responds.'}
          </p>
          <button className="btn-primary" onClick={() => setShowTemplates(true)}>Send a template</button>
        </div>
      ) : (
        <>
          {tab === 'reply' && attachment && (
            <div className="composer-attachment">
              {previewUrl
                ? <img className="attachment-thumb" src={previewUrl} alt={attachment.name} />
                : <span className="attachment-icon">{attachment.type === 'video/mp4' ? '🎬' : '📄'}</span>}
              <div className="attachment-info">
                <span className="attachment-name">{attachment.name}</span>
                <span className="attachment-size">{fileSizeLabel(attachment.size)}</span>
              </div>
              <button className="attachment-remove" onClick={() => setAttachment(null)} title="Remove attachment">×</button>
            </div>
          )}
          <div className={`composer-input ${tab === 'note' ? 'note-mode' : ''}`}>
            {tab === 'reply' && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACH_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={pickFile}
                />
                <button
                  className="btn-attach"
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach an image or document"
                >📎</button>
              </>
            )}
            <textarea
              ref={textareaRef}
              rows={2}
              placeholder={
                tab === 'note' ? 'Write a private note for your team… (customer never sees this)'
                : attachment ? 'Add a caption… (optional)'
                : 'Type a reply… ("/" for saved replies)'
              }
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
            />
            <button className="btn-send" onClick={send} disabled={sending || (!text.trim() && !(tab === 'reply' && attachment))}>
              {sending ? '…' : (tab === 'note' ? 'Add note' : 'Send')}
            </button>
          </div>
        </>
      )}

      {showTemplates && (
        <TemplateModal
          conversationId={conversation._id}
          onClose={() => setShowTemplates(false)}
          onSent={data => { setShowTemplates(false); onSent(data); }}
        />
      )}
    </div>
  );
}

// ─── new chat modal ──────────────────────────────────────────────────────────

// Start a conversation by phone number. The server finds or creates the
// contact and thread; a customer who has never written in starts with no 24h
// window, so the composer lands on the template flow automatically.
function NewChatModal({ onClose, onStarted }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [starting, setStarting] = useState(false);

  const digits = phone.replace(/\D/g, '');
  const valid = digits.length >= 7 && digits.length <= 15;

  const start = async () => {
    if (!valid || starting) return;
    setStarting(true);
    try {
      const { data } = await api.post('/inbox/conversations', { phone, name: name.trim() });
      onStarted(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start the chat');
      setStarting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal new-chat-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New chat</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <label className="new-chat-label">Phone number (with country code)</label>
        <input
          autoFocus
          placeholder="e.g. 263 77 123 4567"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') start(); }}
        />
        {phone && !valid && <div className="new-chat-hint">Enter 7–15 digits including the country code</div>}
        <label className="new-chat-label">Name (optional — used if this is a new contact)</label>
        <input
          placeholder="Customer name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') start(); }}
        />
        <button className="btn-primary" onClick={start} disabled={!valid || starting}>
          {starting ? 'Starting…' : 'Start chat'}
        </button>
      </div>
    </div>
  );
}

// ─── template picker modal ───────────────────────────────────────────────────

function TemplateModal({ conversationId, onClose, onSent }) {
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/inbox/templates')
      .then(r => setTemplates(r.data))
      .catch(err => {
        toast.error(err.response?.data?.message || 'Failed to load templates');
        setTemplates([]);
      });
  }, []);

  const paramCount = template => {
    const body = (template.components || []).find(c => c.type === 'BODY');
    const matches = (body?.text || '').match(/{{\d+}}/g);
    return matches ? matches.length : 0;
  };

  const pick = t => {
    setSelected(t);
    setParams(Array(paramCount(t)).fill(''));
  };

  const send = async () => {
    if (!selected || sending) return;
    if (params.some(p => !p.trim())) return toast.error('Fill in all template parameters');
    setSending(true);
    try {
      const { data } = await api.post(`/inbox/conversations/${conversationId}/messages`, {
        type: 'template',
        template_name: selected.name,
        language: selected.language,
        category: selected.category,
        params: params.map(p => p.trim()),
      });
      if (data.held) {
        toast('Template is on hold until tokens are added', { icon: '⏸' });
        window.dispatchEvent(new Event('token-balance-refresh'));
        onClose();
      }
      else {
        toast.success('Template sent');
        onSent(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Template send failed');
    } finally {
      setSending(false);
    }
  };

  const bodyText = selected
    ? ((selected.components || []).find(c => c.type === 'BODY')?.text || '')
    : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal template-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send a template message</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        {templates === null ? (
          <p className="modal-loading">Loading templates…</p>
        ) : !selected ? (
          <div className="template-list">
            {templates.length === 0 && <p className="modal-loading">No approved templates found.</p>}
            {templates.map(t => (
              <button key={`${t.name}-${t.language}`} className="template-item" onClick={() => pick(t)}>
                <strong>{t.name}</strong>
                <span className="template-lang">{t.language}</span>
                <span className="template-body">{(t.components || []).find(c => c.type === 'BODY')?.text?.slice(0, 100)}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="template-send">
            <button className="btn-link" onClick={() => setSelected(null)}>‹ Back to templates</button>
            <p className="template-preview">{bodyText}</p>
            {params.map((p, i) => (
              <input
                key={i}
                placeholder={`Parameter {{${i + 1}}}`}
                value={p}
                onChange={e => setParams(prev => prev.map((v, j) => j === i ? e.target.value : v))}
              />
            ))}
            <button className="btn-primary" onClick={send} disabled={sending}>
              {sending ? 'Sending…' : `Send "${selected.name}"`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── contact side panel ──────────────────────────────────────────────────────

function ContactPanel({ visible, onClose, conversation, agents, labels, currentUser, onPatch }) {
  const contact = conversation.contact || {};
  const assignedId = conversation.assigned_to?._id || conversation.assigned_to || '';
  const activeLabels = (conversation.labels || []).map(l => l._id || l);

  const toggleLabel = id => {
    const next = activeLabels.includes(id)
      ? activeLabels.filter(l => l !== id)
      : [...activeLabels, id];
    onPatch({ labels: next });
  };

  return (
    <aside className={`contact-panel ${visible ? 'visible' : ''}`}>
      <div className="panel-header">
        <h3>Details</h3>
        <button className="btn-close" onClick={onClose}>×</button>
      </div>

      <div className="panel-section">
        <div className="panel-avatar">{(contact.name || contact.phone || '?')[0].toUpperCase()}</div>
        <div className="panel-contact-name">{contact.name || 'Unknown'}</div>
        <div className="panel-contact-detail">{contact.phone}</div>
        {contact.email && <div className="panel-contact-detail">{contact.email}</div>}
        {contact.opt_in_status === false && <div className="panel-optout">⛔ Opted out</div>}
      </div>

      <div className="panel-section">
        <label className="panel-label">Status</label>
        <div className="panel-status-buttons">
          {['open', 'pending', 'resolved'].map(s => (
            <button
              key={s}
              className={`panel-status-btn ${conversation.status === s ? 'active ' + s : ''}`}
              onClick={() => onPatch({ status: s })}
            >{s[0].toUpperCase() + s.slice(1)}</button>
          ))}
        </div>
      </div>

      <div className="panel-section">
        <label className="panel-label">Assigned to</label>
        <select
          value={assignedId}
          onChange={e => onPatch({ assigned_to: e.target.value || null })}
        >
          <option value="">Unassigned</option>
          {agents.map(a => (
            <option key={a._id} value={a._id}>
              {a.name}{String(a._id) === String(currentUser._id) ? ' (me)' : ''}{a.team_role === 'owner' ? ' — owner' : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="panel-section">
        <label className="panel-label">Bot / human</label>
        <div className="panel-mode-row">
          <span className={`mode-indicator ${conversation.mode}`}>
            {conversation.mode === 'human' ? '🙋 Human handling' : '🤖 Bot active'}
          </span>
          <button
            className="btn-secondary"
            onClick={() => onPatch({ mode: conversation.mode === 'human' ? 'bot' : 'human' })}
          >
            {conversation.mode === 'human' ? 'Hand back to bot' : 'Take over'}
          </button>
        </div>
      </div>

      {labels.length > 0 && (
        <div className="panel-section">
          <label className="panel-label">Labels</label>
          <div className="panel-labels">
            {labels.map(l => (
              <button
                key={l._id}
                className={`panel-tag ${activeLabels.includes(l._id) ? 'active' : ''}`}
                style={l.color ? { '--tag-color': l.color } : undefined}
                onClick={() => toggleLabel(l._id)}
              >{l.name}</button>
            ))}
          </div>
        </div>
      )}

      {contact.notes && (
        <div className="panel-section">
          <label className="panel-label">Contact notes</label>
          <p className="panel-notes">{contact.notes}</p>
        </div>
      )}
    </aside>
  );
}
